/**
 * BaseUIComponent - Abstract base for all UI components with template loading,
 * asset management, and lifecycle hooks. Subclasses implement component-specific behavior.
 */

import { NetRuntime } from '../../multiplayer/NetRuntime.js';
import { AssetPath } from '../../storage/storageProvider.js';
import { shortUUID } from '../../utils/uuid.js';
import { UIAssetLoader } from '../ui-loader.js';

import type { UIElement } from '../UIElement.js';
import type { UILayout } from '../UILayout.js';
import type { UIManager } from '../UIManager.js';



/**
 * Catalog category for a UI component. Used by the genesys-ui-kit skill to
 * group widgets and help coding agents pick the right one.
 */
export type UIComponentCategory =
  | 'hud'
  | 'menu'
  | 'inventory'
  | 'navigation'
  | 'feedback'
  | 'input'
  | 'data'
  | 'control';

/**
 * Static catalog metadata that every BaseUIComponent subclass should declare.
 *
 * Example:
 * ```ts
 * static metadata: UIComponentMetadata = {
 *   displayName: 'Progress Bar',
 *   category: 'hud',
 *   summary: 'Themed progress indicator with animated value transitions.',
 *   useCases: ['health', 'mana', 'stamina', 'experience', 'loading'],
 *   optionsType: 'ProgressBarOptions',
 *   assetPaths: {
 *     template: '@engine/assets/ui/templates/progress-bar.html',
 *     styles: '@engine/assets/ui/styles/progress-bar.css',
 *   },
 * };
 * ```
 *
 * The genesys-ui-kit catalog generator walks UI_COMPONENT_REGISTRY and reads
 * this static field from each class.
 */
export interface UIComponentMetadata {
  /** Human-readable label, e.g. "Progress Bar". */
  displayName: string;
  /** Bucket used to group widgets in the catalog. */
  category: UIComponentCategory;
  /** One-line description of what the widget is for. */
  summary: string;
  /** Keywords / scenarios that should match this widget (e.g. "health", "xp"). */
  useCases: string[];
  /** Name of the TS options interface for this widget (e.g. "ProgressBarOptions"). */
  optionsType: string;
  /** Asset paths shipped with the engine. */
  assetPaths: {
    template: string;
    styles: string;
  };
  /**
   * Marks the widget as superseded by a newer one. The catalog generator
   * surfaces this in a dedicated "Deprecated" section and the genesys-ui-kit
   * skill instructs agents not to use deprecated widgets for new work.
   *
   * Set this when an existing widget is being replaced by a Figma-derived
   * variant that changes its public API or visual footprint enough that
   * existing projects shouldn't be silently migrated. Style-only refreshes
   * should replace the widget in place and leave this unset.
   */
  deprecated?: {
    /** Class name of the replacement widget (e.g. "ProgressBarV2"). */
    replacedBy: string;
    /** Short rationale shown in the catalog. */
    reason: string;
  };
}

/**
 * Constructor type for a BaseUIComponent subclass that exposes static catalog
 * metadata. The catalog generator and the UI_COMPONENT_REGISTRY are typed
 * against this so missing metadata is caught at build time.
 */
export type UIComponentClass = (new (
  ui: UIManager,
  options: any
) => BaseUIComponent<any>) & {
  metadata: UIComponentMetadata;
};

/**
 * Base options that all UI components should support
 */
export interface BaseUIComponentOptions {
  /**
   * Position of the component on screen. Use `'none'` for widgets composed
   * inside another widget — it skips the auto-positioning class that's only
   * valid at the top level of `uiContainer`.
   */
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right' | 'center' | 'none';
  /** Whether the component is initially visible */
  visible?: boolean;
  /** Custom CSS classes to add to the component */
  customClasses?: string[];
  /** Custom inline styles to apply */
  customStyles?: Record<string, string>;
}

/**
 * Abstract base class for all UI components
 * Provides common functionality and enforces consistent interface
 */
export abstract class BaseUIComponent<TOptions extends BaseUIComponentOptions = BaseUIComponentOptions> {
  protected ui: UIManager;
  protected options: Required<TOptions>;
  protected layout: UILayout | null = null;
  protected elements: Map<string, UIElement> = new Map();
  protected isInitialized = false;
  protected isVisible = true;
  /** Child components composed inside this widget; destroyed automatically. */
  private childComponents: BaseUIComponent<any>[] = [];

  constructor(ui: UIManager, options: TOptions) {
    this.ui = ui;
    this.options = this.mergeWithDefaults(options);
  }

  /**
   * Initialize the component - loads assets and creates layout
   * Must be called before using the component
   */
  public async initialize(): Promise<void> {
    // UI components have no meaning on a headless dedicated server.
    if (NetRuntime.isHeadless()) {
      return;
    }

    if (this.isInitialized) {
      console.warn(`Component ${this.constructor.name} is already initialized`);
      return;
    }

    try {
      // Load component assets
      const { template, styles } = await this.loadAssets();

      // Create layout with initial data
      this.layout = this.ui.createLayout(this.getComponentId(), {
        template,
        styles,
        data: this.getInitialData()
      });

      // Apply positioning and styling
      this.applyPositioning();
      this.applyCustomStyling();

      // Cache element references
      this.cacheElements();

      // Perform component-specific initialization
      await this.onInitialize();

      // Set initial visibility
      if (!this.options.visible) {
        this.hide();
      }

      this.isInitialized = true;
    } catch (error) {
      throw new Error(`Failed to initialize ${this.constructor.name}: ${error}`);
    }
  }

  /**
   * Show the component
   */
  public show(): void {
    if (!this.isInitialized) {
      console.warn(`Cannot show ${this.constructor.name} - not initialized`);
      return;
    }
    this.layout?.getElement().show();
    this.isVisible = true;
    this.onShow();
  }

  /**
   * Hide the component
   */
  public hide(): void {
    if (!this.isInitialized) {
      console.warn(`Cannot hide ${this.constructor.name} - not initialized`);
      return;
    }
    this.layout?.getElement().hide();
    this.isVisible = false;
    this.onHide();
  }

  /**
   * Toggle component visibility
   */
  public toggle(): void {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Check if component is visible
   */
  public getIsVisible(): boolean {
    return this.isVisible;
  }

  /**
   * Check if component is initialized
   */
  public getIsInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Get the root layout element
   */
  public getElement(): UIElement | null {
    return this.layout?.getElement() ?? null;
  }

  /**
   * Get a cached element by key
   */
  protected getElementByKey(key: string): UIElement | undefined {
    return this.elements.get(key);
  }

  /**
   * Update component data and re-render
   * Note: This triggers onDataUpdate for component-specific handling
   */
  public updateData(data: Record<string, any>): void {
    if (!this.isInitialized || !this.layout) {
      console.warn(`Cannot update data for ${this.constructor.name} - not initialized`);
      return;
    }

    // Note: UILayout doesn't have updateData method, so we handle updates in onDataUpdate
    this.onDataUpdate(data);
  }

  /**
   * Set the position of the component using CSS properties
   * @param styles - CSS positioning properties (e.g. { top: '20px', left: '20px' })
   * @param selector - Optional CSS selector to target a specific child element
   */
  public setPosition(styles: Record<string, string>, selector?: string): void {
    if (!this.isInitialized || !this.layout) {
      console.warn(`Cannot set position for ${this.constructor.name} - not initialized`);
      return;
    }

    let targetElement: HTMLElement | null = null;

    if (selector) {
      // Target a specific child element
      targetElement = this.layout.querySelector(selector) as HTMLElement;
      if (!targetElement) {
        console.warn(`Cannot find element with selector "${selector}" in ${this.constructor.name}`);
        return;
      }
    } else {
      // Target the root element of the layout
      const rootElement = this.layout.getElement();
      targetElement = (rootElement as any)?.element as HTMLElement;
    }

    if (targetElement) {
      // Apply positioning styles
      Object.entries(styles).forEach(([property, value]) => {
        targetElement!.style.setProperty(property, value);
      });
    }
  }

  /**
   * Destroy the component and clean up resources
   */
  public destroy(): void {
    if (!this.isInitialized) {
      return;
    }

    this.onDestroy();

    // Destroy any composed child components (registered via mountChild).
    for (const child of this.childComponents) {
      child.destroy();
    }
    this.childComponents = [];

    this.layout?.remove();
    this.layout = null;
    this.elements.clear();
    this.isInitialized = false;
    this.isVisible = false;
  }

  /**
   * Compose another UI component inside this one. Creates the child with
   * `position: 'none'`, initializes it, moves its root element into `slot`,
   * and tracks it so `destroy()` cleans it up automatically.
   */
  protected async mountChild<TChild extends BaseUIComponent<any>>(
    ComponentClass: new (ui: UIManager, options: any) => TChild,
    options: BaseUIComponentOptions & Record<string, any>,
    slot: HTMLElement,
  ): Promise<TChild> {
    const child = new ComponentClass(this.ui, { ...options, position: 'none' });
    await child.initialize();
    const wrapper = child.getElement();
    if (wrapper) {
      // `wrapper.element` is the `.ui-layout` container div. We want the actual
      // template root (first non-script child) so we don't add an extra wrapper level.
      const layoutEl = (wrapper as any).element as HTMLElement;
      let templateRoot: HTMLElement | null = null;
      for (let i = 0; i < layoutEl.children.length; i++) {
        const c = layoutEl.children[i] as HTMLElement;
        if (c.tagName.toLowerCase() !== 'script') { templateRoot = c; break; }
      }
      slot.appendChild(templateRoot ?? layoutEl);
    }
    this.childComponents.push(child);
    return child;
  }

  // Abstract methods that subclasses must implement

  /**
   * Get the template and styles file paths for this component
   */
  protected abstract getAssetPaths(): { templatePath: string; stylesPath: string };

  /**
   * Get the default options for this component type
   */
  protected abstract getDefaultOptions(): Required<TOptions>;

  /**
   * Get initial data for template interpolation
   */
  protected abstract getInitialData(): Record<string, any>;

  // Virtual methods that subclasses can override

  /**
   * Get the unique ID for this component instance
   * Default implementation uses kebab-case class name + UUID
   * Override if you need custom ID generation logic
   */
  protected getComponentId(): string {
    // Convert class name from PascalCase to kebab-case
    const className = this.constructor.name;
    const kebabCase = className
      .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
      .toLowerCase();

    return `${kebabCase}-${shortUUID()}`;
  }

  /**
   * Cache frequently accessed elements after layout creation
   */
  protected cacheElements(): void {
    // Override in subclasses to cache specific elements
  }

  /**
   * Called after successful initialization
   */
  protected async onInitialize(): Promise<void> {
    // Override in subclasses for custom initialization
  }

  /**
   * Called when component is shown
   */
  protected onShow(): void {
    // Override in subclasses for show logic
  }

  /**
   * Called when component is hidden
   */
  protected onHide(): void {
    // Override in subclasses for hide logic
  }

  /**
   * Called when data is updated
   */
  protected onDataUpdate(data: Record<string, any>): void {
    // Override in subclasses for data update logic
  }

  /**
   * Called before component destruction
   */
  protected onDestroy(): void {
    // Override in subclasses for cleanup logic
  }

  // Private helper methods

  private async loadAssets(): Promise<{ template: string; styles: string }> {
    const { templatePath, stylesPath } = this.getAssetPaths();
    return await UIAssetLoader.loadUIAsset(AssetPath.fromString(templatePath), AssetPath.fromString(stylesPath));
  }

  private mergeWithDefaults(options: TOptions): Required<TOptions> {
    const defaults = this.getDefaultOptions();
    return { ...defaults, ...options } as Required<TOptions>;
  }

  private applyPositioning(): void {
    if (!this.layout || !this.options.position || this.options.position === 'none') return;

    const layoutElement = this.layout.getElement();

    // Find the actual template root (skip Vite injected scripts and other non-template elements)
    let templateRoot: HTMLElement | null = null;

    for (let i = 0; i < layoutElement.element.children.length; i++) {
      const child = layoutElement.element.children[i] as HTMLElement;

      // Skip script tags (Vite development scripts)
      if (child.tagName.toLowerCase() === 'script') {
        continue;
      }

      // Skip elements with Vite-specific attributes
      if (child.hasAttribute('src') && child.getAttribute('src')?.includes('vite')) {
        continue;
      }

      // This should be our template root
      templateRoot = child;
      break;
    }

    if (templateRoot) {
      templateRoot.classList.add(this.options.position);
    }
  }

  private applyCustomStyling(): void {
    if (!this.layout) return;

    const element = this.layout.getElement();

    // Apply custom classes
    if (this.options.customClasses) {
      this.options.customClasses.forEach(className => {
        element.addClass(className);
      });
    }

    // Apply custom styles using direct style property access
    if (this.options.customStyles) {
      Object.entries(this.options.customStyles).forEach(([property, value]) => {
        // Get the underlying HTML element and set style directly
        const htmlElement = (element as any).element as HTMLElement;
        if (htmlElement && htmlElement.style) {
          htmlElement.style.setProperty(property, value);
        }
      });
    }
  }

  /**
   * Utility method to create element wrappers for HTML elements
   */
  protected createElementWrapper(htmlElement: HTMLElement | null): UIElement {
    if (!htmlElement) {
      throw new Error(`HTML element not found in ${this.constructor.name} layout`);
    }

    // Create a wrapper that delegates to the HTML element
    const wrapper = {
      element: htmlElement,
      setText: (text: string) => {
        htmlElement.textContent = text;
        return wrapper;
      },
      addClass: (className: string) => {
        htmlElement.classList.add(className);
        return wrapper;
      },
      removeClass: (className: string) => {
        htmlElement.classList.remove(className);
        return wrapper;
      },
      show: () => {
        htmlElement.style.display = 'block';
        return wrapper;
      },
      hide: () => {
        htmlElement.style.display = 'none';
        return wrapper;
      },
      setStyle: (property: string, value: string) => {
        htmlElement.style.setProperty(property, value);
        return wrapper;
      }
    } as UIElement;

    return wrapper;
  }
}